import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Input,
  Form,
  Space,
  message,
  Spin,
  notification,
  Select,
  Tag,
} from "antd";
import { FaPlus, FaEdit, FaSearch } from "react-icons/fa";
import { apiHandle } from "../../config/apiHandle";

const { Option } = Select;

const FinishingManagement = () => {
  const [finishings, setFinishings] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingFinishing, setEditingFinishing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchFinishings();
  }, []);

  // ✅ Fetch all finishings (with status)
  const fetchFinishings = async () => {
    setLoading(true);
    try {
      const response = await apiHandle.get("admin/get-finishing");
      setFinishings(response?.data?.data || []);
    } catch (error) {
      message.error("Failed to fetch finishings.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Add or Update finishings (with status)
  const handleAddOrUpdateFinishings = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingFinishing) {
        await apiHandle.post(`admin/edit-finishings/${editingFinishing._id}`, {
          name: values.name,
          status: values.status,
        });
        notification.success({ message: "finishing updated successfully" });
      } else {
        await apiHandle.post("admin/add-finishing", {
          name: values.name,
          status: values.status || "Active", // Default status to Active
        });
        message.success("Finishing added successfully");
      }

      fetchFinishings();
      form.resetFields();
      setIsModalVisible(false);
      setEditingFinishing(null);
    } catch (error) {
      notification.error({
        message: "Please fill in all required fields correctly.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Edit Finishings
  const handleEdit = (record) => {
    setEditingFinishing(record);
    form.setFieldsValue({
      name: record.name,
      status: record.status || "Active",
    });
    setIsModalVisible(true);
  };

  // ✅ Filter Finishing for Search
  const filteredFinishings = finishings.filter((finishing) =>
    finishing.name.toLowerCase().includes(search.toLowerCase())
  );

  // ✅ Table Columns (With Status)
  const columns = [
    {
      title: "Finishing Name",
      key: "name",
      dataIndex: "name",
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Space>
          <Button icon={<FaEdit />} onClick={() => handleEdit(record)} />
        </Space>
      ),
    },
  ];

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h1 className="text-3xl font-bold text-center mb-4">Finishing Management</h1>

      {/* ✅ Search Bar */}
      <div className="flex justify-between my-4">
        <Input
          placeholder="Search by Finishing name"
          prefix={<FaSearch />}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: "300px" }}
        />
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => {
            form.resetFields();
            form.setFieldsValue({ status: "Active" }); // Default status
            setIsModalVisible(true);
          }}
        >
          Add Finishing
        </Button>
      </div>

      {/* ✅ Table with Sizes & Status */}
      {loading ? (
        <Spin size="large" className="flex justify-center" />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredFinishings}
          pagination={{ pageSize: 15 }}
          rowKey="_id"
        />
      )}

      {/* ✅ Modal for Add & Edit */}
      <Modal
        title={editingFinishing ? "Edit Finishing" : "Add Finishing"}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingFinishing(null);
          form.resetFields();
        }}
        onOk={handleAddOrUpdateFinishings}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Finishing Name"
            rules={[{ required: true, message: "Please enter a finishing name!" }]}
          >
            <Input />
          </Form.Item>

          {/* ✅ Status Selection */}
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select status!" }]}
          >
            <Select>
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FinishingManagement;
